import { useState } from 'react';
import type { ClickableScreenScene, ClickableScreenZone } from '../types';
import { percentCenterRectStyle } from '../systems/clickable-screen-utils';
import styles from './clickable-screen-overlay.module.css';

interface ClickableScreenOverlayProps {
  scene: ClickableScreenScene;
  zones: ClickableScreenZone[];
  onZoneClick: (zone: ClickableScreenZone) => void;
  clickedZoneId?: string | null;
  disabled?: boolean;
}

export function ClickableScreenOverlay({
  scene,
  zones,
  onZoneClick,
  clickedZoneId = null,
  disabled = false,
}: ClickableScreenOverlayProps): JSX.Element | null {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (!zones.length) return null;

  return (
    <div className={styles.overlay} aria-hidden={disabled}>
      {zones.map((zone) => {
        const isClicked = clickedZoneId === zone.id;
        const isLocked = Boolean(clickedZoneId) && !isClicked;
        return (
          <button
            key={`${scene.id}-${zone.id}`}
            type="button"
            className={`${styles.zone} ${isClicked ? styles.zoneClicked : ''}`}
            style={percentCenterRectStyle(zone.position)}
            disabled={disabled || isLocked}
            aria-label={zone.label}
            onClick={() => onZoneClick(zone)}
            onMouseEnter={() => setHoveredId(zone.id)}
            onMouseLeave={() => setHoveredId((id) => (id === zone.id ? null : id))}
            onFocus={() => setHoveredId(zone.id)}
            onBlur={() => setHoveredId((id) => (id === zone.id ? null : id))}
          >
            <span className={`${styles.corner} ${styles.cornerTL}`} aria-hidden />
            <span className={`${styles.corner} ${styles.cornerTR}`} aria-hidden />
            <span className={`${styles.corner} ${styles.cornerBL}`} aria-hidden />
            <span className={`${styles.corner} ${styles.cornerBR}`} aria-hidden />
            {(hoveredId === zone.id || isClicked) && (
              <span className={`${styles.label} cn-zoom-ui`}>{zone.label}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
